import { useIsMutating, useMutationState } from '@tanstack/react-query';
import { useState } from 'react';

export type SaveState = 'idle' | 'saving' | 'saved' | 'error';

/**
 * What the card dialog tells about its autosave: any mutation in flight = saving; otherwise the
 * outcome of the most recent one made while the dialog has been open (a failure stays visible
 * until the next successful save). Derived from the mutation cache, so every editor of the
 * dialog (title, Markdown, labels, dates, ...) is covered without each reporting by hand.
 */
export function useSaveStatus(): SaveState {
  const [openedAt] = useState(() => Date.now());
  const pending = useIsMutating();
  const recent = useMutationState({
    select: (mutation) => ({
      status: mutation.state.status,
      at: mutation.state.submittedAt,
    }),
  }).filter((entry) => entry.at >= openedAt && entry.status !== 'idle');
  if (pending > 0) return 'saving';
  const last = recent.reduce<(typeof recent)[number] | null>(
    (best, entry) => (best === null || entry.at >= best.at ? entry : best),
    null,
  );
  if (last === null) return 'idle';
  return last.status === 'error' ? 'error' : 'saved';
}
